/**
 * src/pages/checkout/page.tsx
 *
 * Stripe-powered checkout page — completely independent of WooCommerce's own checkout.
 * Flow:
 *   1. User arrives here after completing the booking steps (booking state in location.state)
 *   2. We call POST /orders to create a WC order (status: pending)
 *   3. We call POST /stripe/create-payment-intent with the order total
 *   4. Stripe Elements renders a payment form with the client_secret
 *   5. On payment success, Stripe webhook fires → WC order → processing, Jobber job created
 *   6. React redirects to /checkout/success?order_id=XXX
 *
 * The Stripe publishable key is fetched from GET /stripe/config so you only
 * ever enter it once in WP Admin → Cleario → Settings.
 */

import { useEffect, useState, useCallback } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import {
  loadStripe,
  type Stripe,
  type StripeElements,
} from "@stripe/stripe-js";
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import { stripeApi, ordersApi } from "../../lib/api";
import type { BookingState } from "../booking/page";

// ── Types ──────────────────────────────────────────────────────────────────────
interface CheckoutLocationState {
  booking: BookingState;
  total: number;
}

// ── Root page — fetches Stripe config + creates WC order ───────────────────────
export default function CheckoutPage() {
  const location  = useLocation();
  const navigate  = useNavigate();
  const state     = (location.state as CheckoutLocationState) ?? null;

  const booking: BookingState | null = state?.booking ?? null;
  const total: number                = state?.total   ?? 0;

  const [stripePromise, setStripePromise] = useState<Promise<Stripe | null> | null>(null);
  const [clientSecret, setClientSecret]   = useState("");
  const [orderId, setOrderId]             = useState<number | null>(null);
  const [error, setError]                 = useState<string | null>(null);
  const [loading, setLoading]             = useState(true);

  useEffect(() => {
    if (!booking || !total) {
      navigate("/book", { replace: true });
      return;
    }

    (async () => {
      try {
        // 1. Load Stripe publishable key from WordPress
        const config = await stripeApi.getConfig();
        if (!config.publishable_key) throw new Error("Stripe is not configured. Please call us on 03335 775445.");
        setStripePromise(loadStripe(config.publishable_key));

        // 2. Create WC order (pending, no payment yet)
        const order = await ordersApi.create(booking as never);
        setOrderId(order.order_id);

        // 3. Create Stripe PaymentIntent for the order amount
        const pi = await stripeApi.createPaymentIntent({
          amount:   total,
          order_id: order.order_id,
          email:    booking.email,
          name:     `${booking.firstName} ${booking.lastName}`.trim(),
        });
        setClientSecret(pi.client_secret);
      } catch (err) {
        setError((err as Error)?.message ?? "Something went wrong setting up payment.");
      } finally {
        setLoading(false);
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) return <CheckoutShell><LoadingState /></CheckoutShell>;
  if (error)   return <CheckoutShell><ErrorState message={error} /></CheckoutShell>;
  if (!stripePromise || !clientSecret) return null;

  return (
    <CheckoutShell>
      <Elements
        stripe={stripePromise}
        options={{
          clientSecret,
          appearance: {
            theme: "stripe",
            variables: {
              colorPrimary:       "#6CC412",
              colorBackground:    "#ffffff",
              colorText:          "#09245b",
              colorDanger:        "#ef4444",
              fontFamily:         "system-ui, sans-serif",
              borderRadius:       "12px",
              spacingUnit:        "4px",
            },
          },
        }}
      >
        <CheckoutForm
          booking={booking!}
          total={total}
          orderId={orderId}
        />
      </Elements>
    </CheckoutShell>
  );
}

// ── Stripe payment form ────────────────────────────────────────────────────────
function CheckoutForm({
  booking,
  total,
  orderId,
}: {
  booking: BookingState;
  total: number;
  orderId: number | null;
}) {
  const stripe   = useStripe();
  const elements = useElements();
  const navigate = useNavigate();

  const [paying,   setPaying]   = useState(false);
  const [payError, setPayError] = useState<string | null>(null);

  const handlePay = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setPaying(true);
    setPayError(null);

    const { error } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url:        `${window.location.origin}/checkout/success?order_id=${orderId}`,
        receipt_email:     booking.email,
        payment_method_data: {
          billing_details: {
            name:  `${booking.firstName} ${booking.lastName}`.trim(),
            email: booking.email,
            phone: booking.phone,
            address: {
              line1:       `${booking.houseNumber} ${booking.addressLine1}`.trim(),
              line2:       booking.addressLine2,
              city:        booking.city,
              postal_code: booking.postcode,
              country:     "GB",
            },
          },
        },
      },
      // redirect: "if_required" — handle 3DS redirects automatically
    });

    // If we reach here it means Stripe did NOT redirect (payment failed)
    if (error) {
      setPayError(
        error.type === "card_error" || error.type === "validation_error"
          ? (error.message ?? "Payment failed.")
          : "An unexpected error occurred. Please try again."
      );
      setPaying(false);
    }
  }, [stripe, elements, booking, orderId]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 max-w-5xl mx-auto">

      {/* ── Left: payment form ── */}
      <div className="lg:col-span-3">
        <div className="bg-white rounded-3xl cartoon-card p-8">
          <h2 className="font-black text-cl-dark text-xl mb-6 flex items-center gap-2">
            <i className="ri-lock-line text-cl-green"></i>
            Secure Payment
          </h2>

          <form onSubmit={handlePay}>
            <PaymentElement
              options={{
                layout: "tabs",
                business: { name: "Cleario" },
              }}
            />

            {payError && (
              <div className="mt-4 p-4 bg-red-50 border-2 border-red-200 rounded-xl text-red-700 text-sm font-semibold flex items-start gap-2">
                <i className="ri-error-warning-line mt-0.5 flex-shrink-0"></i>
                {payError}
              </div>
            )}

            <button
              type="submit"
              disabled={!stripe || paying}
              className="w-full mt-6 bg-cl-green hover:bg-green-700 disabled:opacity-60 disabled:cursor-not-allowed text-white font-black py-4 rounded-xl text-base transition-all flex items-center justify-center gap-2 cartoon-btn-green"
            >
              {paying ? (
                <>
                  <span className="w-5 h-5 rounded-full border-2 border-white/40 border-t-white animate-spin"></span>
                  Processing payment…
                </>
              ) : (
                <>
                  <i className="ri-lock-fill"></i>
                  Pay £{total.toFixed(2)} now
                </>
              )}
            </button>
          </form>

          {/* Trust badges */}
          <div className="flex items-center justify-center gap-6 mt-6 pt-5 border-t-2 border-gray-100">
            {[
              { icon: "ri-shield-check-line", label: "256-bit SSL" },
              { icon: "ri-bank-card-line",    label: "Stripe Secure" },
              { icon: "ri-lock-2-line",       label: "PCI Compliant" },
            ].map((b) => (
              <div key={b.label} className="flex items-center gap-1.5 text-gray-400 text-xs font-semibold">
                <i className={`${b.icon} text-sm`}></i>
                {b.label}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Right: order summary ── */}
      <div className="lg:col-span-2">
        <div className="bg-white rounded-3xl cartoon-card p-6 sticky top-24">
          <h3 className="font-black text-cl-dark text-base mb-5 flex items-center gap-2">
            <i className="ri-file-list-3-line text-cl-green"></i>
            Order Summary
          </h3>

          {orderId && (
            <div className="bg-cl-tint rounded-xl px-4 py-2.5 mb-4 flex items-center justify-between">
              <span className="text-xs font-black text-gray-400 uppercase tracking-widest">Order</span>
              <span className="font-black text-cl-dark text-sm">#{orderId}</span>
            </div>
          )}

          <div className="space-y-3 mb-4">
            <div className="flex justify-between text-sm">
              <span className="text-cl-dark/70 font-semibold">{booking.serviceTitle}</span>
              <span className="font-black text-cl-dark">£{booking.selectedOptionPrice.toFixed(2)}</span>
            </div>
            {booking.cartItems.map((item) => (
              <div key={item.id} className="flex justify-between text-sm">
                <span className="text-cl-dark/70 font-semibold">
                  {item.label} × {item.quantity}
                </span>
                <span className="font-black text-cl-dark">
                  £{(item.quantity * item.unitPrice).toFixed(2)}
                </span>
              </div>
            ))}
          </div>

          <div className="border-t-2 border-gray-100 pt-4">
            <div className="flex justify-between items-center">
              <span className="font-black text-cl-dark text-base">Total (inc. VAT)</span>
              <span className="font-black text-cl-green text-2xl">£{total.toFixed(2)}</span>
            </div>
          </div>

          {/* Booking details */}
          <div className="mt-5 space-y-2.5">
            {[
              { icon: "ri-map-pin-line",      label: booking.postcode },
              { icon: "ri-calendar-line",     label: booking.date ? new Date(booking.date).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "long" }) : "" },
              { icon: "ri-time-line",         label: booking.timeSlotLabel },
            ].filter((r) => r.label).map((row) => (
              <div key={row.icon} className="flex items-center gap-2.5 text-sm">
                <i className={`${row.icon} text-cl-green flex-shrink-0`}></i>
                <span className="text-cl-dark font-semibold">{row.label}</span>
              </div>
            ))}
          </div>

          <Link
            to="/book"
            className="mt-5 flex items-center gap-1.5 text-xs text-cl-dark/40 hover:text-cl-dark font-semibold transition-colors"
          >
            <i className="ri-arrow-left-line"></i>
            Back to booking
          </Link>
        </div>
      </div>
    </div>
  );
}

// ── Success page (Stripe redirects here after payment) ─────────────────────────
export function CheckoutSuccessPage() {
  const location = useLocation();
  const orderId  = new URLSearchParams(location.search).get("order_id");
  // Stripe also appends payment_intent and payment_intent_client_secret to the URL
  const piStatus = new URLSearchParams(location.search).get("redirect_status");

  if (piStatus === "failed") {
    return (
      <CheckoutShell>
        <div className="max-w-md mx-auto text-center py-12">
          <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-5">
            <i className="ri-close-line text-red-500 text-4xl"></i>
          </div>
          <h1 className="text-3xl font-black text-cl-dark mb-3">Payment failed</h1>
          <p className="text-cl-dark/60 font-semibold mb-6">Your card was not charged. Please try again or call us.</p>
          <Link to="/book" className="bg-cl-green text-white font-black px-8 py-4 rounded-xl inline-flex items-center gap-2">
            <i className="ri-arrow-left-line"></i>Try again
          </Link>
        </div>
      </CheckoutShell>
    );
  }

  return (
    <CheckoutShell>
      <div className="max-w-xl mx-auto">
        <div className="text-center mb-8">
          <div className="w-24 h-24 bg-cl-green rounded-full flex items-center justify-center mx-auto mb-6 border-4 border-[#4a8c0a] cartoon-shadow-green">
            <i className="ri-check-line text-white text-5xl"></i>
          </div>
          <div className="inline-flex items-center gap-2 bg-cl-lime rounded-full px-4 py-1.5 mb-4 thick-border cartoon-shadow-sm">
            <span className="w-2 h-2 bg-cl-green rounded-full animate-pulse"></span>
            <span className="text-cl-dark text-xs font-black uppercase tracking-widest">Payment Confirmed</span>
          </div>
          <h1 className="text-4xl font-black text-cl-dark mb-3">You're all booked in!</h1>
          <p className="text-cl-dark/60 font-semibold text-lg">
            Payment received. We'll call to confirm your arrival window.
          </p>
        </div>

        {orderId && (
          <div className="bg-cl-dark rounded-2xl px-7 py-5 mb-6 cartoon-card text-center">
            <p className="text-white/60 text-sm font-semibold mb-1">Your booking reference</p>
            <p className="text-cl-lime font-black text-3xl tracking-widest">#{orderId}</p>
          </div>
        )}

        <div className="bg-white rounded-2xl cartoon-card p-7 mb-6">
          <h3 className="font-black text-cl-dark text-base mb-5 flex items-center gap-2">
            <i className="ri-route-line text-cl-green"></i>
            What happens next
          </h3>
          <div className="flex flex-col gap-4">
            {[
              { icon: "ri-phone-line",     title: "We'll call to confirm",    desc: "Our team will call within 2 hours to confirm your exact arrival window." },
              { icon: "ri-mail-line",      title: "Confirmation email",        desc: "A receipt and booking confirmation has been sent to your email." },
              { icon: "ri-map-2-line",     title: "Live tracking on the day",  desc: "You'll get a text with a live tracker so you can follow your team." },
              { icon: "ri-file-text-line", title: "Waste transfer note",       desc: "Your legal waste transfer note arrives by email within 24 hrs." },
            ].map((item, i) => (
              <div key={i} className="flex items-start gap-3">
                <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-cl-lime border border-cl-green flex-shrink-0 text-cl-green">
                  <i className={`${item.icon} text-sm`}></i>
                </span>
                <div>
                  <p className="text-sm font-black text-cl-dark">{item.title}</p>
                  <p className="text-xs text-gray-500 font-semibold leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          <Link to="/" className="flex-1 bg-cl-dark text-white font-black py-4 rounded-xl text-center text-sm cartoon-btn flex items-center justify-center gap-2">
            <i className="ri-home-line"></i>Back to Homepage
          </Link>
          {orderId && (
            <Link to="/account" className="flex-1 bg-cl-green text-white font-black py-4 rounded-xl text-center text-sm flex items-center justify-center gap-2">
              <i className="ri-user-3-line"></i>View in My Account
            </Link>
          )}
        </div>
      </div>
    </CheckoutShell>
  );
}

// ── Shell wrapper ──────────────────────────────────────────────────────────────
function CheckoutShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-cl-tint">
      <nav className="bg-white border-b-4 border-cl-dark sticky top-0 z-50">
        <div className="max-w-screen-xl mx-auto px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-1">
            <span className="text-2xl font-black tracking-tight text-cl-dark">
              Clear<span className="text-cl-green">io</span>
            </span>
            <span className="w-2.5 h-2.5 rounded-full bg-cl-green mb-4 inline-block border-2 border-cl-dark"></span>
          </Link>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-1.5 text-xs font-bold text-cl-dark/50">
              <i className="ri-lock-line text-cl-green"></i>
              Secure checkout
            </div>
            <a href="tel:03335775445" className="hidden sm:flex items-center gap-1.5 text-sm font-bold text-cl-dark hover:text-cl-green transition-colors">
              <i className="ri-phone-fill text-cl-green text-sm"></i>
              03335 775445
            </a>
          </div>
        </div>
      </nav>

      <div className="max-w-screen-xl mx-auto px-6 lg:px-8 pt-10 pb-20">
        {children}
      </div>

      <div className="bg-white border-t-2 border-gray-100 py-4">
        <div className="max-w-screen-xl mx-auto px-8 flex flex-col sm:flex-row items-center justify-between gap-2">
          <p className="text-xs text-gray-400 font-semibold">
            Waste Carrier Licence: CBDU 634440 &nbsp;·&nbsp; © Cleario 2026
          </p>
          <div className="flex items-center gap-4">
            <a href="/terms"   className="text-xs text-gray-400 hover:text-cl-dark font-semibold">Terms</a>
            <a href="/privacy" className="text-xs text-gray-400 hover:text-cl-dark font-semibold">Privacy</a>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Loading / error states ─────────────────────────────────────────────────────
function LoadingState() {
  return (
    <div className="flex flex-col items-center justify-center py-24 gap-4">
      <div className="w-12 h-12 rounded-full border-4 border-cl-green border-t-transparent animate-spin"></div>
      <p className="text-cl-dark/60 font-semibold text-sm">Setting up your secure checkout…</p>
    </div>
  );
}

function ErrorState({ message }: { message: string }) {
  return (
    <div className="max-w-lg mx-auto py-12">
      {/* Reassurance banner */}
      <div className="bg-cl-lime border-2 border-cl-green rounded-2xl px-6 py-4 mb-6 flex items-start gap-3">
        <i className="ri-checkbox-circle-fill text-cl-green text-2xl flex-shrink-0 mt-0.5"></i>
        <div>
          <p className="font-black text-cl-dark text-sm mb-0.5">Your booking details have been received</p>
          <p className="text-cl-dark/70 text-xs font-semibold leading-relaxed">We have your request — our team will be in touch shortly to confirm and take payment over the phone.</p>
        </div>
      </div>

      {/* Error detail (collapsed) */}
      <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 mb-6 flex items-start gap-2">
        <i className="ri-error-warning-line text-red-400 text-base flex-shrink-0 mt-0.5"></i>
        <p className="text-red-700 text-xs font-semibold leading-relaxed">{message}</p>
      </div>

      {/* Actions */}
      <div className="bg-white rounded-2xl cartoon-card p-6">
        <h3 className="font-black text-cl-dark text-base mb-4">Complete your booking now</h3>
        <div className="flex flex-col gap-3">
          <a href="tel:03335775445" className="flex items-center gap-3 bg-cl-green text-white font-black px-5 py-4 rounded-xl text-sm transition-colors hover:bg-green-700">
            <span className="w-9 h-9 flex items-center justify-center rounded-xl bg-white/20 flex-shrink-0">
              <i className="ri-phone-fill text-lg"></i>
            </span>
            <div>
              <p className="leading-none">Call us to pay &amp; confirm</p>
              <p className="text-white/70 text-xs font-semibold mt-0.5">03335 775445</p>
            </div>
          </a>
          <a href="https://wa.me/447533720995" target="_blank" rel="noopener noreferrer"
            className="flex items-center gap-3 bg-[#25D366] text-white font-black px-5 py-4 rounded-xl text-sm transition-colors hover:bg-[#1fbb5a]">
            <span className="w-9 h-9 flex items-center justify-center rounded-xl bg-white/20 flex-shrink-0">
              <i className="ri-whatsapp-line text-lg"></i>
            </span>
            <div>
              <p className="leading-none">WhatsApp us instead</p>
              <p className="text-white/70 text-xs font-semibold mt-0.5">Quick reply guaranteed</p>
            </div>
          </a>
          <Link to="/book" className="flex items-center justify-center gap-2 bg-cl-dark/10 text-cl-dark font-black px-5 py-3.5 rounded-xl text-sm">
            <i className="ri-arrow-left-line"></i>Back to Booking
          </Link>
        </div>
      </div>
    </div>
  );
}